import React, { useState, useEffect } from 'react'
import { Input } from '@/components/ui/Input'
import { FormItem, FormContainer } from '@/components/ui/Form'
import { Alert } from '@/components/ui/Alert'
import { useAuth } from '@/hooks/useAuth'
import { useNavigate, useLocation } from 'react-router-dom'
import { Formik, Form, Field, FormikHelpers } from 'formik'
import * as Yup from 'yup'
import classNames from 'classnames'

interface CommonProps {
    className?: string;
}

interface SignInFormProps extends CommonProps {
    disableSubmit?: boolean
}

type SignInFormSchema = {
    email: string
    password: string
}

const validationSchema = Yup.object().shape({
    email: Yup.string()
        .email('Please input valid email address')
        .required('Please enter your email'),
    password: Yup.string().required('Please enter your password'),
})

const SignInForm = (props: SignInFormProps) => {
    const {
        disableSubmit = false,
        className,
    } = props

    const [error, setError] = useState<string>('')
    const navigate = useNavigate()
    const location = useLocation()
    const { login } = useAuth()

    // 从URL参数中读取错误信息
    useEffect(() => {
        // 使用 URLSearchParams 解析查询参数
        const queryParams = new URLSearchParams(location.search)
        const errorParam = queryParams.get('error')
        
        if (errorParam) {
            // 解码错误信息
            const decodedError = decodeURIComponent(errorParam)
            setError(decodedError)
            
            // 清除URL中的错误参数
            navigate(location.pathname, { replace: true })
        }
    }, [location, navigate])

    const validateEmail = (email: string): boolean => {
        return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
    }

    const onSignIn = async (
        values: SignInFormSchema,
        { setSubmitting }: FormikHelpers<SignInFormSchema>
    ) => {
        const { email, password } = values
        setError('')

        try {
            // Email validation
            if (!validateEmail(email)) {
                setError('Please input valid email address')
                setSubmitting(false)
                return
            }

            // Password validation 
            if (!password) {
                setError('Password cannot be empty')
                setSubmitting(false)
                return
            }

            console.log('Attempting login with:', email.toLowerCase())
            const result = await login(email.toLowerCase(), password)
            console.log('Login result:', result)
            
            if (result === 'SUCCESS') {
                console.log('Login successful, navigating to dashboard...')
                navigate('/dashboard')
            } else if (result === 'USER IS NOT EXIST, PLEASE LOGIN') {
                setError('You are in AP Mode and there is no login information. Please switch to other WiFi or 4G.')
            }
        } catch (err) {
            console.error('Login error:', err)
            setError(err instanceof Error ? err.message : 'Login failed')
        } finally {
            setSubmitting(false)
        }
    }

    return (
        <div className={className}>
            {error && (
                <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200">
                    <p className="text-red-700 text-sm">{error}</p>
                </div>
            )}
            <Formik
                initialValues={{
                    email: '',
                    password: '',
                }}
                validationSchema={validationSchema}
                onSubmit={onSignIn}
            >
                {({ isSubmitting, errors, touched }) => (
                    <Form className="space-y-6">
                        <FormContainer>
                            <div>
                                <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                                    Email Address
                                </label>
                                <Field
                                    type="email"
                                    id="email"
                                    name="email"
                                    placeholder="Enter your email"
                                    className={classNames(
                                        "w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-colors",
                                        {
                                            "border-red-300 focus:ring-red-500 focus:border-red-500": errors.email && touched.email,
                                            "border-gray-300 focus:ring-blue-500 focus:border-blue-500": !(errors.email && touched.email),
                                        }
                                    )}
                                />
                                {errors.email && touched.email && (
                                    <div className="text-red-600 text-sm mt-1">{errors.email}</div>
                                )}
                            </div>
                            <div>
                                <label htmlFor="password" className="block text-sm font-semibold text-gray-700 mb-2">
                                    Password
                                </label>
                                <Field
                                    type="password"
                                    id="password"
                                    name="password"
                                    placeholder="Enter your password"
                                    className={classNames(
                                        "w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-colors",
                                        {
                                            "border-red-300 focus:ring-red-500 focus:border-red-500": errors.password && touched.password,
                                            "border-gray-300 focus:ring-blue-500 focus:border-blue-500": !(errors.password && touched.password),
                                        }
                                    )}
                                />
                                {errors.password && touched.password && (
                                    <div className="text-red-600 text-sm mt-1">{errors.password}</div>
                                )}
                            </div>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className={classNames(
                                    "w-full py-3 px-4 rounded-lg font-semibold text-white transition-all duration-200 mt-8",
                                    {
                                        "bg-green-500 hover:bg-green-600 active:bg-green-700 shadow-lg hover:shadow-xl": !isSubmitting,
                                        "bg-gray-400 cursor-not-allowed": isSubmitting
                                    }
                                )}
                            >
                                {isSubmitting ? 'Signing in...' : 'Sign In'}
                            </button>
                        </FormContainer>
                    </Form>
                )}
            </Formik>
        </div>
    )
}

export default SignInForm
